import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { login } from "../services/api"; // Import the API function for admin login

interface AuthData {
  email_or_username: string;
  password: string;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  token: string;
}

// Async thunk for admin login
export const loginAdminAsync = createAsyncThunk<UserData, AuthData, { rejectValue: string }>(
  "auth/login",
  async (credentials: AuthData, { rejectWithValue }) => {
    try {
      const response = await login(credentials);
      const userData = response.data;

      // Set token expiration (current time + 24 hours)
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;

      localStorage.setItem("authToken", userData.token);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("tokenExpiry", expiryTime.toString()); // Store expiry time

      return userData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);


const token = localStorage.getItem("authToken");
const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")!) : null;
const tokenExpiry = localStorage.getItem("tokenExpiry");

const isTokenExpired = tokenExpiry ? Date.now() > Number(tokenExpiry) : true;

const initialState = {
  token: isTokenExpired ? null : token,
  user: isTokenExpired ? null : user,
  loading: false,
  error: null as string | null,
};


const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
      localStorage.removeItem("authToken"); // Remove token on logout
      localStorage.removeItem("user");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdminAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAdminAsync.fulfilled, (state, action) => {
        state.loading = false;

        const tokenExpiry = localStorage.getItem("tokenExpiry");
        const isTokenExpired = tokenExpiry ? Date.now() > Number(tokenExpiry) : true;

        if (isTokenExpired) {
          state.user = null;
          state.token = null;
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
          localStorage.removeItem("tokenExpiry");
        } else {
          state.user = action.payload;
          state.token = action.payload.token;
        }
      })
      .addCase(loginAdminAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
